/**
 * Reduce a thrown value to what a failure card may show and what it may
 * only carry (REQ-UI-037 / REQ-UI-191).
 *
 * Kept apart from :file:`error-fallback.tsx` so that file exports only
 * components (fast refresh) and so the boundaries can log the same
 * one-line summary the card displays.
 */

/** A thrown value reduced to what the card may show and what it may only carry. */
export interface ErrorDescription {
  /** ``Name: message`` for an Error; a string form otherwise. Never includes a stack. */
  summary: string;
  /** The full stack (or the summary when there is none) for ``data-debug``. */
  debug: string;
}

/**
 * Reduce a thrown value to a one-line summary and a debug string.
 *
 * The summary is what the ``<details>`` disclosure shows; the debug string
 * is what rides ``data-debug``. Keeping the split here, rather than in the
 * card, lets the boundaries log the same summary they display.
 */
export function describeError(error: unknown): ErrorDescription {
  if (error instanceof Error) {
    const summary = error.message ? `${error.name}: ${error.message}` : error.name;
    return { summary, debug: error.stack ?? summary };
  }
  if (error === undefined || error === null) {
    return { summary: "Unknown error", debug: "Unknown error" };
  }
  if (typeof error === "string") {
    return { summary: error, debug: error };
  }
  let summary: string;
  try {
    summary = JSON.stringify(error);
  } catch {
    summary = String(error);
  }
  return { summary, debug: summary };
}
